// Team calendars (/v1/teams/{id}/calendars): which weekdays are workdays and
// which dates are holidays. A deployment's "workday N" schedule and its
// window count workdays in one; without one it counts Monday to Friday.
// Everyone on the team reads them; a maintainer changes them. These routes
// exist only while the server has deployments on; listCalendars tells the
// two apart.

import { getJSON, optionalJSON, request, sendJSON } from "./http.js";

const BASE = "/v1/teams";

export type Weekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export const WEEKDAYS: ReadonlyArray<{ value: Weekday; label: string }> = [
  { value: "mon", label: "Mon" },
  { value: "tue", label: "Tue" },
  { value: "wed", label: "Wed" },
  { value: "thu", label: "Thu" },
  { value: "fri", label: "Fri" },
  { value: "sat", label: "Sat" },
  { value: "sun", label: "Sun" },
];

export interface Calendar {
  id: string;
  teamId: string;
  name: string;
  /** The weekdays that are workdays, in week order. */
  workdays: Weekday[];
  /** Dates (YYYY-MM-DD) that are no workdays, in date order. */
  holidays: string[];
  created: string;
  updated: string;
}

export interface CalendarRequest {
  /** 1 to 64 characters, unique in the team. */
  name: string;
  /** At least one weekday. */
  workdays: Weekday[];
  /** At most 1000 dates. */
  holidays?: string[];
}

/** listCalendars returns the team's calendars by name (viewer+), or null while deployments are off. */
export async function listCalendars(teamId: string): Promise<Calendar[] | null> {
  const res = await optionalJSON<{ items: Calendar[] }>(
    `${BASE}/${encodeURIComponent(teamId)}/calendars`,
  );
  return res ? res.items : null;
}

/** getCalendar returns one calendar (viewer+). */
export function getCalendar(teamId: string, calendarId: string): Promise<Calendar> {
  return getJSON<Calendar>(calPath(teamId, calendarId));
}

/** createCalendar adds a calendar to the team (maintainer+). */
export function createCalendar(teamId: string, req: CalendarRequest): Promise<Calendar> {
  return sendJSON<Calendar>(`${BASE}/${encodeURIComponent(teamId)}/calendars`, "POST", req);
}

/**
 * updateCalendar changes a calendar (maintainer+). holidays replaces the
 * whole list. Every deployment that counts in it is planned again.
 */
export function updateCalendar(
  teamId: string,
  calendarId: string,
  req: Partial<CalendarRequest>,
): Promise<Calendar> {
  return sendJSON<Calendar>(calPath(teamId, calendarId), "PATCH", req);
}

/** deleteCalendar removes a calendar (maintainer+); 409 calendar_in_use while a deployment uses it. */
export async function deleteCalendar(teamId: string, calendarId: string): Promise<void> {
  await request(calPath(teamId, calendarId), { method: "DELETE" });
}

function calPath(teamId: string, calendarId: string): string {
  return `${BASE}/${encodeURIComponent(teamId)}/calendars/${encodeURIComponent(calendarId)}`;
}
